import setupLandingEffects from '@/components/home/landingEffects';
import HeroSection from '@/components/home/HeroSection.vue';
import ShowcaseSection from '@/components/home/ShowcaseSection.vue';
import FeaturesSection from '@/components/home/FeaturesSection.vue';
import CommunitySection from '@/components/home/CommunitySection.vue';
import ScrollCard from '@/components/home/ScrollCard.vue';
import SharingSection from '@/components/home/SharingSection.vue';
import WorkflowSection from '@/components/home/WorkflowSection.vue';
import SocialLinks from '@/components/home/SocialLinks.vue';
import Component from 'vue-class-component';
import Vue from 'vue';
import { Watch } from 'vue-property-decorator';
import Login from '@/components/Login.vue';
import Registration from '@/components/Registration.vue';
import TestCodeDialog from '@/components/TestCodeDialog.vue';
import Legal from '@/components/Legal.vue';
import Privacy from '@/components/Privacy.vue';
import store from '@/store';
import UserModule from '@/store/modules/User';
import { getModule } from 'vuex-module-decorators';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';

const userModule = getModule(UserModule, store);

@Component({
  components: {
    HeroSection,
    ShowcaseSection,
    FeaturesSection,
    CommunitySection,
    ScrollCard,
    SharingSection,
    WorkflowSection,
    SocialLinks,

    Login,
    Registration,
    TestCodeDialog,
    Legal,
    Privacy,
  },
})
export default class Home extends Vue {
  get areAuthPanelsCollapsed(): boolean {
    return typeof this.authPanel !== 'number';
  }

  get hasActiveAuthPanel(): boolean {
    return typeof this.authPanel === 'number';
  }

  get useLightDotsTheme(): boolean {
    return (this.currentSectionIndex === 0 && this.useDarkHeroDecorator) || (this.currentSectionIndex >= 1 && this.currentSectionIndex <= 3);
  }

  get useLightLoginTheme(): boolean {
    return this.currentSectionIndex >= 1 && this.currentSectionIndex <= 3;
  }

  public overlay = false;
  public authPanel: number | null = 0;
  public legalOpen = false;
  public privacyOpen = false;
  public currentSectionIndex = 0;
  public landingSectionIds = ['section-1', 'section-2', 'section-3', 'section-4', 'section-5', 'section-6'];
  public section4Screens = [1, 2, 3, 4, 5, 6];
  public heroNextCtaVisible = false;
  public useDarkHeroDecorator = false;
  public dissolvingNextCtaIndex: number | null = null;
  public launchingNextCtaIndex: number | null = null;
  public pressedNextCtaIndex: number | null = null;
  public manualSectionNavUntil = 0;
  public landingCancelSnap: (() => void) | null = null;
  private dotNavigationRafId: number | null = null;
  private isAuthTransitionRunning = false;
  public landingRafId: number | null = null;
  public landingResizeHandler: (() => void) | null = null;
  public landingScrollHandler: (() => void) | null = null;
  public landingCleanup: Array<() => void> = [];
  public loginLabelInterval: number | null = null;
  public introTimers: number[] = [];
  private pressedNextCtaTimer: number | null = null;
  private starfieldAnimation: number | null = null;
  private stars: Array<{ x: number; y: number; size: number; speed: number; opacity: number; twinkle: number }> = [];
  private doodles: Array<{ x: number; y: number; type: string; size: number; speed: number; rotation: number; opacity: number }> = [];
  private comets: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    opacity: number;
    trail: Array<{ x: number; y: number; opacity: number }>;
    life: number;
    maxLife: number;
  }> = [];

  public created() {
    // Set splash phase immediately on homepage to avoid black flash before intro appears.
    document.body.classList.add('intro-sequence', 'intro-phase--splash');
    document.body.classList.add('homepage-scrollbar');
    document.documentElement.classList.add('homepage-scrollbar');
    document.body.classList.remove('intro-phase--logo-out', 'intro-phase--burst', 'intro-phase--decorator-in', 'intro-phase--ui-reveal');
    document.body.classList.remove('login-attention-ready');
    this.heroNextCtaVisible = false;
    this.useDarkHeroDecorator = false;
    this.forceScrollToTop();
  }

  public mounted() {
    this.forceScrollToTop();
    window.setTimeout(() => this.forceScrollToTop(), 0);
    requestAnimationFrame(() => this.forceScrollToTop());
    this.syncPolicyModalWithRoute();
    setupLandingEffects.call(this);
  }

  public beforeDestroy() {
    this.cancelDotNavigation();
    this.detachAuthEscapeHandler();
    document.getElementById('hide-scrollbar-style')?.remove();
    document.body.classList.remove('homepage-scrollbar');
    document.documentElement.classList.remove('homepage-scrollbar');
    this.landingCleanup.forEach((cleanup) => cleanup());
    this.landingCleanup = [];
    document.body.classList.remove('section4-is-dragging');

    if (this.landingScrollHandler) {
      window.removeEventListener('scroll', this.landingScrollHandler);
    }
    if (this.landingResizeHandler) {
      window.removeEventListener('resize', this.landingResizeHandler);
    }
    if (this.landingRafId) {
      cancelAnimationFrame(this.landingRafId);
      this.landingRafId = null;
    }
    if (this.loginLabelInterval) {
      clearInterval(this.loginLabelInterval);
      this.loginLabelInterval = null;
    }
    this.introTimers.forEach((timer) => clearTimeout(timer));
    this.introTimers = [];
    if (this.pressedNextCtaTimer !== null) {
      clearTimeout(this.pressedNextCtaTimer);
      this.pressedNextCtaTimer = null;
    }
    document.body.classList.remove(
      'intro-sequence',
      'intro-phase--splash',
      'intro-phase--logo-out',
      'intro-phase--burst',
      'intro-phase--decorator-in',
      'intro-phase--ui-reveal',
    );
    document.body.classList.remove('login-attention-ready');
    this.heroNextCtaVisible = false;

    if (this.starfieldAnimation) {
      cancelAnimationFrame(this.starfieldAnimation);
      this.starfieldAnimation = null;
    }
  }

  public onModalLeave() {
    if (this.starfieldAnimation) {
      cancelAnimationFrame(this.starfieldAnimation);
      this.starfieldAnimation = null;
    }
  }
  @Watch('overlay')
  onOverlayChange(newVal: boolean) {
    if (newVal) {
      // Ensure test-code modal is closed when opening login overlay.
      userModule.setProvideCode(false);
      this.authPanel = null;
      const style = document.createElement('style');
      style.id = 'hide-scrollbar-style';
      style.innerHTML = `
        html { scrollbar-width: none; -ms-overflow-style: none; }
        html::-webkit-scrollbar, body::-webkit-scrollbar { display: none; }
            `;
      document.head.appendChild(style);
      window.addEventListener('keydown', this.onAuthOverlayKeydown);

      this.$nextTick(() => {
        this.initStarfield();
      });
    } else {
      const style = document.getElementById('hide-scrollbar-style');
      if (style) {
        style.remove();
      }
      this.detachAuthEscapeHandler();

      if (this.starfieldAnimation) {
        cancelAnimationFrame(this.starfieldAnimation);
        this.starfieldAnimation = null;
      }
    }
  }
  public collapseAuthPanels() {
    if (this.hasActiveAuthPanel) {
      this.authPanel = null;
    }
  }

  private detachAuthEscapeHandler() {
    window.removeEventListener('keydown', this.onAuthOverlayKeydown);
  }

  private onAuthOverlayKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.overlay || this.isAuthTransitionRunning || this.hasActiveDialog()) {
      return;
    }

    event.preventDefault();
    if (this.hasActiveAuthPanel) {
      this.collapseAuthPanels();
      return;
    }

    this.overlay = false;
  }

  private hasActiveDialog(): boolean {
    return Boolean(document.querySelector('.v-dialog__content--active'));
  }

  @Watch('$route.name')
  onRouteNameChange() {
    this.syncPolicyModalWithRoute();
  }

  public switchToLoginPanel() {
    this.authPanel = 0;
  }

  public onOverlayBackdropClick() {
    if (this.isAuthTransitionRunning) {
      return;
    }
    this.overlay = false;
  }

  public async onAuthSuccess() {
    if (this.isAuthTransitionRunning) {
      return;
    }

    this.isAuthTransitionRunning = true;
    this.overlay = true;
    this.legalOpen = false;
    this.privacyOpen = false;

    const redirectRoute = userModule.redirectRoute || '/sessions';

    // Start one continuous handoff layer together with the space zoom.
    window.dispatchEvent(new Event('raws-preloader-start'));
    await new Promise((resolve) => window.setTimeout(resolve, 920));

    userModule.resetRedirectRoute();
    try {
      await this.$router.push(redirectRoute);
    } catch (error) {
      const isExpectedRedirect = isNavigationFailure(error, NavigationFailureType.redirected);
      const isDuplicateNavigation = isNavigationFailure(error, NavigationFailureType.duplicated);

      if (!isExpectedRedirect && !isDuplicateNavigation) {
        this.isAuthTransitionRunning = false;
        throw error;
      }
    }
  }

  public openLegalModal() {
    this.privacyOpen = false;
    const overlayWasOpen = this.overlay;
    this.overlay = false;
    window.setTimeout(
      () => {
        if (overlayWasOpen && this.overlay) {
          return;
        }
        this.legalOpen = true;
      },
      overlayWasOpen ? 40 : 0,
    );
  }

  public openPrivacyModal() {
    this.legalOpen = false;
    const overlayWasOpen = this.overlay;
    this.overlay = false;
    window.setTimeout(
      () => {
        if (overlayWasOpen && this.overlay) {
          return;
        }
        this.privacyOpen = true;
      },
      overlayWasOpen ? 40 : 0,
    );
  }

  public scrollToLandingSection(sectionIndex: number) {
    const targetId = this.landingSectionIds[sectionIndex];
    if (!targetId) {
      return;
    }
    const targetSection = document.getElementById(targetId);
    if (!targetSection) {
      return;
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targetTop = sectionIndex === 0 ? 0 : targetSection.offsetTop;
    this.cancelDotNavigation();
    this.landingCancelSnap?.();
    this.currentSectionIndex = sectionIndex;
    const startTop = window.scrollY;
    const distance = targetTop - startTop;
    if (prefersReducedMotion || Math.abs(distance) < window.innerHeight * 1.5) {
      this.manualSectionNavUntil = prefersReducedMotion ? 0 : performance.now() + 1400;
      window.scrollTo({ top: targetTop, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      return;
    }

    const duration = Math.min(1800, Math.max(950, Math.abs(distance) * 0.48));
    const startedAt = performance.now();
    this.manualSectionNavUntil = startedAt + duration + 400;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = progress * progress * (3 - 2 * progress);
      window.scrollTo(0, startTop + distance * eased);
      this.dotNavigationRafId = progress < 1 ? requestAnimationFrame(tick) : null;
    };
    this.dotNavigationRafId = requestAnimationFrame(tick);
  }

  public cancelDotNavigation() {
    if (this.dotNavigationRafId !== null) {
      cancelAnimationFrame(this.dotNavigationRafId);
      this.dotNavigationRafId = null;
    }
  }

  public scrollToLandingSectionCta(sectionIndex: number) {
    const targetId = this.landingSectionIds[sectionIndex];
    if (!targetId) {
      return;
    }
    const targetSection = document.getElementById(targetId);
    if (!targetSection) {
      return;
    }
    if (this.launchingNextCtaIndex !== null || this.dissolvingNextCtaIndex !== null) {
      return;
    }
    this.cancelDotNavigation();
    this.landingCancelSnap?.();

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isTopSection = sectionIndex === 0;
    const targetTop = isTopSection ? 0 : targetSection.offsetTop;
    const isHeroLaunch = sectionIndex === 1 && this.currentSectionIndex === 0 && !prefersReducedMotion;

    if (isHeroLaunch) {
      this.launchingNextCtaIndex = sectionIndex;
      const launchDuration = 980;
      const scrollLeadMs = 160;
      const scrollDuration = 1560;
      const startTop = window.scrollY;
      const distance = targetTop - startTop;
      const startedAt = performance.now();
      const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

      this.manualSectionNavUntil = startedAt + scrollLeadMs + scrollDuration + 900;

      window.setTimeout(() => {
        this.currentSectionIndex = sectionIndex;

        const tick = (now: number) => {
          const progress = Math.min(1, (now - (startedAt + scrollLeadMs)) / scrollDuration);
          const eased = easeInOutCubic(progress);
          window.scrollTo(0, startTop + distance * eased);

          if (progress < 1) {
            requestAnimationFrame(tick);
            return;
          }

          window.scrollTo({ top: targetTop, behavior: 'auto' });
        };

        requestAnimationFrame(tick);
      }, scrollLeadMs);

      window.setTimeout(() => {
        if (this.launchingNextCtaIndex === sectionIndex) {
          this.launchingNextCtaIndex = null;
        }
      }, launchDuration);
      return;
    }

    if (sectionIndex === 2 || sectionIndex === 3) {
      this.currentSectionIndex = sectionIndex;
      this.pressedNextCtaIndex = sectionIndex;
      if (this.pressedNextCtaTimer !== null) {
        clearTimeout(this.pressedNextCtaTimer);
      }
      this.pressedNextCtaTimer = window.setTimeout(() => {
        if (this.pressedNextCtaIndex === sectionIndex) {
          this.pressedNextCtaIndex = null;
        }
        this.pressedNextCtaTimer = null;
      }, 240);
    } else {
      this.currentSectionIndex = sectionIndex;
      this.dissolvingNextCtaIndex = sectionIndex;
      window.setTimeout(() => {
        if (this.dissolvingNextCtaIndex === sectionIndex) {
          this.dissolvingNextCtaIndex = null;
        }
      }, 900);
    }

    if (prefersReducedMotion || isTopSection) {
      this.manualSectionNavUntil = prefersReducedMotion ? 0 : performance.now() + 1400;
      window.scrollTo({ top: targetTop, behavior: 'auto' });
      return;
    }

    if (sectionIndex >= 4) {
      this.manualSectionNavUntil = performance.now() + 2200;
      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      });
      return;
    }

    const startTop = window.scrollY;
    const distance = targetTop - startTop;
    const duration = Math.min(2800, Math.max(1900, Math.abs(distance) * 1.08));
    const startedAt = performance.now();
    this.manualSectionNavUntil = startedAt + duration + 700;

    const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = easeInOutCubic(progress);
      window.scrollTo(0, startTop + distance * eased);

      if (progress < 1) {
        requestAnimationFrame(tick);
        return;
      }

      window.scrollTo({ top: targetTop, behavior: 'auto' });
    };

    requestAnimationFrame(tick);
  }

  private syncPolicyModalWithRoute() {
    const routeName = this.$route?.name ? String(this.$route.name) : '';
    if (routeName === 'terms') {
      this.overlay = false;
      this.privacyOpen = false;
      this.legalOpen = true;
      return;
    }
    if (routeName === 'privacy') {
      this.overlay = false;
      this.legalOpen = false;
      this.privacyOpen = true;
    }
  }

  private forceScrollToTop() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }

  private initStarfield() {
    const canvas = this.$refs.starfield as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    this.stars = [];
    const numStars = Math.floor((canvas.width * canvas.height) / 10000);

    for (let i = 0; i < numStars; i++) {
      this.stars.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 2 + 0.5,
        speed: Math.random() * 0.5 + 0.1,
        opacity: Math.random() * 0.8 + 0.2,
        twinkle: Math.random() * Math.PI * 2,
      });
    }

    this.doodles = [];
    const numDoodles = Math.floor((canvas.width * canvas.height) / 50000);
    const doodleTypes = ['circle', 'square', 'triangle', 'star', 'heart'];

    for (let i = 0; i < numDoodles; i++) {
      this.doodles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        type: doodleTypes[Math.floor(Math.random() * doodleTypes.length)],
        size: Math.random() * 8 + 4,
        speed: Math.random() * 0.3 + 0.1,
        rotation: Math.random() * Math.PI * 2,
        opacity: Math.random() * 0.6 + 0.2,
      });
    }

    this.comets = [];

    this.animateStarfield();
  }

  private animateStarfield() {
    const canvas = this.$refs.starfield as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.fillStyle = '#030303';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    if (Math.random() < 0.005) {
      this.spawnComet();
    }

    this.comets = this.comets.filter((comet) => {
      comet.x += comet.vx;
      comet.y += comet.vy;
      comet.life++;
      comet.trail.push({ x: comet.x, y: comet.y, opacity: 1 });

      if (comet.trail.length > 20) {
        comet.trail.shift();
      }

      comet.trail.forEach((point) => {
        point.opacity *= 0.95;
      });

      for (let i = 0; i < comet.trail.length - 1; i++) {
        const point = comet.trail[i];
        const nextPoint = comet.trail[i + 1];

        const dx = nextPoint.x - point.x;
        const dy = nextPoint.y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const steps = Math.max(1, Math.floor(distance / 2));

        for (let j = 0; j <= steps; j++) {
          const t = j / steps;
          const trailX = Math.floor(point.x + dx * t);
          const trailY = Math.floor(point.y + dy * t);

          ctx.fillStyle = `rgba(255, 255, 255, ${point.opacity * 0.3})`;
          ctx.fillRect(trailX, trailY, 2, 2);
        }
      }

      const pixelSize = Math.max(2, Math.floor(comet.size));
      const halfSize = Math.floor(pixelSize / 2);

      ctx.fillStyle = `rgba(255, 255, 255, ${comet.opacity})`;
      ctx.fillRect(Math.floor(comet.x - halfSize), Math.floor(comet.y - halfSize), pixelSize, pixelSize);

      const glowSize = Math.max(4, Math.floor(comet.size * 2));
      const glowHalfSize = Math.floor(glowSize / 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${comet.opacity * 0.2})`;
      ctx.fillRect(Math.floor(comet.x - glowHalfSize), Math.floor(comet.y - glowHalfSize), glowSize, glowSize);

      return comet.life < comet.maxLife && comet.x > -50 && comet.x < canvas.width + 50 && comet.y > -50 && comet.y < canvas.height + 50;
    });

    this.stars.forEach((star) => {
      star.twinkle += 0.02;
      const twinkleOpacity = star.opacity * (0.5 + 0.5 * Math.sin(star.twinkle));

      const pixelSize = Math.max(1, Math.floor(star.size));
      const halfSize = Math.floor(pixelSize / 2);

      ctx.fillStyle = `rgba(255, 255, 255, ${twinkleOpacity})`;
      ctx.fillRect(Math.floor(star.x - halfSize), Math.floor(star.y - halfSize), pixelSize, pixelSize);

      const glowSize = Math.max(2, Math.floor(star.size * 1.5));
      const glowHalfSize = Math.floor(glowSize / 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${twinkleOpacity * 0.3})`;
      ctx.fillRect(Math.floor(star.x - glowHalfSize), Math.floor(star.y - glowHalfSize), glowSize, glowSize);

      star.y -= star.speed;
      if (star.y < -50) {
        star.y = canvas.height + 50;
        star.x = Math.random() * canvas.width;
      }
    });

    this.doodles.forEach((doodle) => {
      ctx.save();
      ctx.translate(doodle.x, doodle.y);
      ctx.rotate(doodle.rotation);
      ctx.globalAlpha = doodle.opacity;

      this.drawDoodle(ctx, doodle.type, doodle.size);

      ctx.restore();

      doodle.y -= doodle.speed;
      doodle.rotation += 0.01;
      if (doodle.y < -50) {
        doodle.y = canvas.height + 50;
        doodle.x = Math.random() * canvas.width;
        doodle.rotation = Math.random() * Math.PI * 2;
      }
    });

    this.starfieldAnimation = requestAnimationFrame(() => this.animateStarfield());
  }

  private drawDoodle(ctx: CanvasRenderingContext2D, type: string, size: number) {
    const halfSize = size / 2;

    switch (type) {
      case 'circle':
        ctx.beginPath();
        ctx.arc(0, 0, halfSize, 0, Math.PI * 2);
        ctx.fillStyle = '#ff4081';
        ctx.fill();
        break;
      case 'square':
        ctx.fillStyle = '#ff4081';
        ctx.fillRect(-halfSize, -halfSize, size, size);
        break;
      case 'triangle':
        ctx.beginPath();
        ctx.moveTo(0, -halfSize);
        ctx.lineTo(-halfSize, halfSize);
        ctx.lineTo(halfSize, halfSize);
        ctx.closePath();
        ctx.fillStyle = '#ff4081';
        ctx.fill();
        break;
      case 'star':
        ctx.beginPath();
        for (let i = 0; i < 5; i++) {
          const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
          const x = Math.cos(angle) * halfSize;
          const y = Math.sin(angle) * halfSize;
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }

          const innerAngle = angle + Math.PI / 5;
          const innerX = Math.cos(innerAngle) * (halfSize * 0.4);
          const innerY = Math.sin(innerAngle) * (halfSize * 0.4);
          ctx.lineTo(innerX, innerY);
        }
        ctx.closePath();
        ctx.fillStyle = '#ff4081';
        ctx.fill();
        break;
      case 'heart':
        ctx.beginPath();
        ctx.moveTo(0, halfSize * 0.3);
        ctx.bezierCurveTo(-halfSize * 0.5, -halfSize * 0.2, -halfSize, -halfSize * 0.5, 0, -halfSize);
        ctx.bezierCurveTo(halfSize, -halfSize * 0.5, halfSize * 0.5, -halfSize * 0.2, 0, halfSize * 0.3);
        ctx.fillStyle = '#ff4081';
        ctx.fill();
        break;
      default:
        break;
    }
  }

  private spawnComet() {
    const canvas = this.$refs.starfield as HTMLCanvasElement;
    if (!canvas) {
      return;
    }

    const side = Math.floor(Math.random() * 4);
    let x = 0;
    let y = 0;
    let vx = 0;
    let vy = 0;

    switch (side) {
      case 0:
        x = Math.random() * canvas.width;
        y = -20;
        vx = (Math.random() - 0.5) * 2;
        vy = Math.random() * 1 + 0.5;
        break;
      case 1:
        x = canvas.width + 20;
        y = Math.random() * canvas.height;
        vx = -(Math.random() * 1 + 0.5);
        vy = (Math.random() - 0.5) * 2;
        break;
      case 2:
        x = Math.random() * canvas.width;
        y = canvas.height + 20;
        vx = (Math.random() - 0.5) * 2;
        vy = -(Math.random() * 1 + 0.5);
        break;
      case 3:
        x = -20;
        y = Math.random() * canvas.height;
        vx = Math.random() * 1 + 0.5;
        vy = (Math.random() - 0.5) * 2;
        break;
      default:
        break;
    }

    this.comets.push({
      x,
      y,
      vx,
      vy,
      size: Math.random() * 3 + 2,
      opacity: 1,
      trail: [],
      life: 0,
      maxLife: Math.random() * 200 + 100,
    });
  }
}
